import { FC, useEffect, useState } from "react";

import { Autocomplete, Box, TextField, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { PartnerOrgSummary, listPartnerOrg } from "@rubi/api-generated";
import { useNavigate } from "react-router-dom";

import { colors } from "@/common/colors";

interface Props {
  value: string | null;
}

/**
 * Rubicon-staff "Viewing as:" partner picker for the AppBar. The choice is the URL — selecting an org
 * navigates to its report list, so the org segment stays the single source of truth for scope.
 */
const PartnerOrgSwitcher: FC<Props> = ({ value }) => {
  const navigate = useNavigate();
  const [partnerOrgs, setPartnerOrgs] = useState<PartnerOrgSummary[]>([]);
  const [loadFailed, setLoadFailed] = useState(false);

  useEffect(() => {
    let active = true;
    listPartnerOrg()
      .then((res) => {
        if (!active) return;
        if (res.data) {
          setPartnerOrgs(res.data.partner_orgs);
          setLoadFailed(false);
        } else {
          setLoadFailed(true);
        }
      })
      .catch(() => {
        if (active) setLoadFailed(true);
      });
    return () => {
      active = false;
    };
  }, []);

  const selected = partnerOrgs.find((org) => org.id === value) ?? null;

  return (
    <Box sx={{ ml: 2, display: "flex", alignItems: "center" }}>
      <Typography variant="body2" sx={{ mr: 1 }}>
        Viewing as:
      </Typography>
      <Autocomplete
        options={partnerOrgs}
        value={selected}
        onChange={(_event, next) =>
          navigate(next ? `/rubipartner/${next.id}/reports` : "/rubipartner")
        }
        getOptionLabel={(org) => org.title}
        isOptionEqualToValue={(a, b) => a.id === b.id}
        openOnFocus
        selectOnFocus
        handleHomeEndKeys
        size="small"
        renderInput={(params) => (
          <TextField
            {...params}
            error={loadFailed}
            placeholder="Select a partner organization"
            helperText={
              loadFailed ? "Couldn't load partner organizations" : undefined
            }
            slotProps={{
              htmlInput: { ...params.inputProps, "aria-label": "Viewing as" },
            }}
          />
        )}
        sx={(theme) => ({
          ml: 1,
          width: 320,
          "& .MuiInputBase-root": {
            height: "40px",
            color: theme.palette.common.white,
            background: colors.APPBAR_CONTROL_BG,
          },
          "& .MuiSvgIcon-root": { color: theme.palette.common.white },
        })}
        slotProps={{
          paper: {
            sx: (theme) => ({
              background: colors.APPBAR_CONTROL_BG,
              color: theme.palette.common.white,
              "& .MuiAutocomplete-option": {
                '&[aria-selected="true"]': {
                  background: alpha(theme.palette.primary.main, 0.16),
                },
                "&.Mui-focused": {
                  background: alpha(theme.palette.common.white, 0.08),
                },
              },
              "& .MuiAutocomplete-noOptions": {
                color: theme.palette.common.white,
              },
            }),
          },
        }}
      />
    </Box>
  );
};

export default PartnerOrgSwitcher;
